/**
 * Serviço centralizado para gerenciar a Notifications API do navegador
 */

const MAX_SIMULTANEOUS = 3
const SOUND_THROTTLE_MS = 2000 // Throttle de 2 segundos entre bips

/** @type {Notification[]} */
const trackedNotifications = []

/** @type {number | null} */
let lastSoundPlayedAt = null

/** @type {HTMLAudioElement | null} */
let audioElement = null

/**
 * Detecta o formato de áudio suportado pelo navegador
 * @returns {string} Extensão do formato suportado
 */
function detectSupportedAudioFormat() {
  const w = getWindow()
  if (!w) return 'ogg'

  const audio = new w.Audio()

  if (audio.canPlayType('audio/ogg') !== '') {
    return 'ogg'
  }

  if (audio.canPlayType('audio/mpeg') !== '') {
    return 'mp3'
  }

  return 'ogg'
}

/**
 * Inicializa o elemento de áudio com fallback multi-formato
 */
function initAudioElement() {
  if (audioElement) return audioElement

  try {
    const w = getWindow()
    if (!w) return null

    const format = detectSupportedAudioFormat()
    const audioPath = `/audio/fiufiu.${format}`

    audioElement = new w.Audio(audioPath)
    audioElement.volume = 0.3

    audioElement.addEventListener('error', (e) => {
      console.error('[notificationService]', 'initAudioElement: erro ao carregar áudio', {
        path: audioPath,
        error: e
      })
    })

    return audioElement
  } catch (error) {
    console.error('[notificationService]', 'initAudioElement: erro ao criar elemento', error)
    return null
  }
}

/**
 * Toca o som de notificação com throttle para evitar múltiplos bips em sequência
 */
function playNotificationSound() {
  try {
    const now = Date.now()

    if (lastSoundPlayedAt && (now - lastSoundPlayedAt) < SOUND_THROTTLE_MS) {
      return
    }

    const audio = initAudioElement()
    if (!audio) {
      return
    }

    audio.currentTime = 0

    audio.play()
      .catch((error) => {
        console.error('[notificationService]', 'playNotificationSound: erro ao tocar áudio', {
          name: error.name,
          message: error.message,
          code: error.code
        })

        if (error.name === 'NotSupportedError' || error.name === 'NotAllowedError') {
          audioElement = null
        }
      })

    lastSoundPlayedAt = now
  } catch (error) {
    console.error('[notificationService]', 'playNotificationSound: erro ao tocar som', error)
  }
}

/**
 * @returns {Window | null}
 */
function getWindow() {
  if (typeof window === 'undefined') {
    return null
  }
  return window
}

/**
 * Verifica se a API de notificações está disponível no ambiente atual
 * @returns {boolean}
 */
export function isSupported() {
  const w = getWindow()
  return Boolean(w && 'Notification' in w)
}

/**
 * Normaliza o retorno de requestPermission (Promise ou string em navegadores antigos)
 * @param {Promise<NotificationPermission> | NotificationPermission} raw
 * @returns {Promise<NotificationPermission>}
 */
async function normalizePermissionResult(raw) {
  if (typeof raw === 'string') {
    return raw
  }
  try {
    return await raw
  } catch (error) {
    console.error('[notificationService]', 'normalizePermissionResult: erro', error)
    return 'denied'
  }
}

/**
 * Solicita permissão para exibir notificações
 * @returns {Promise<NotificationPermission>}
 */
export async function requestPermission() {
  const w = getWindow()
  if (!w || !('Notification' in w)) {
    return 'denied'
  }
  try {
    const Notif = w.Notification
    const raw = Notif.requestPermission()
    return await normalizePermissionResult(raw)
  } catch (error) {
    console.error('[notificationService]', 'requestPermission: erro', error)
    return 'denied'
  }
}

/**
 * Indica se a permissão atual é "granted"
 * @returns {boolean}
 */
export function hasPermission() {
  const w = getWindow()
  if (!w || !('Notification' in w)) {
    return false
  }
  try {
    return w.Notification.permission === 'granted'
  } catch (error) {
    console.error('[notificationService]', 'hasPermission: erro', error)
    return false
  }
}

/**
 * Remove referências de notificações já encerradas quando a propriedade existe
 */
function pruneClosedTracked() {
  for (let i = trackedNotifications.length - 1; i >= 0; i--) {
    const n = trackedNotifications[i]
    try {
      if (!n) {
        trackedNotifications.splice(i, 1)
        continue
      }
      if ('closed' in n && n.closed === true) {
        trackedNotifications.splice(i, 1)
      }
    } catch (error) {
      console.error('[notificationService]', 'pruneClosedTracked: erro', error)
      trackedNotifications.splice(i, 1)
    }
  }
}

/**
 * Mantém no máximo MAX_SIMULTANEOUS notificações abertas (fecha as mais antigas)
 */
function enforceMaxSimultaneous() {
  pruneClosedTracked()
  while (trackedNotifications.length >= MAX_SIMULTANEOUS) {
    const oldest = trackedNotifications.shift()
    if (!oldest) {
      break
    }
    try {
      oldest.close()
    } catch (error) {
      console.error('[notificationService]', 'enforceMaxSimultaneous: erro ao fechar', error)
    }
  }
}

/**
 * Registra callbacks para remover da fila quando a notificação for fechada
 * @param {Notification} notification
 */
function wireNotificationLifecycle(notification) {
  const detach = () => {
    const idx = trackedNotifications.indexOf(notification)
    if (idx > -1) {
      trackedNotifications.splice(idx, 1)
    }
  }
  try {
    notification.addEventListener('close', detach)
  } catch {
    try {
      notification.onclose = detach
    } catch (error) {
      console.error('[notificationService]', 'lifecycle: não foi possível registrar onclose', error)
    }
  }
}

/**
 * Exibe uma notificação do sistema
 * @param {{ title?: string, body?: string, icon?: string, tag?: string, data?: * }} options
 * @returns {Notification | null}
 */
export function show(options = {}) {
  const w = getWindow()
  if (!w || !('Notification' in w)) {
    return null
  }
  let permission
  try {
    permission = w.Notification.permission
  } catch (error) {
    console.error('[notificationService]', 'show: erro ao ler permissão', error)
    return null
  }
  if (permission !== 'granted') {
    return null
  }
  const { title, body, icon, tag, data } = options
  try {
    enforceMaxSimultaneous()
    const notification = new w.Notification(title || '', {
      body,
      icon,
      tag,
      data,
      requireInteraction: false,
      silent: false,
    })
    trackedNotifications.push(notification)
    wireNotificationLifecycle(notification)
    playNotificationSound()
    return notification
  } catch (error) {
    console.error('[notificationService]', 'show: erro ao criar notificação', error)
    return null
  }
}

/**
 * Retorna as notificações ativas rastreadas por este serviço (quando a API existe)
 * @returns {Notification[]}
 */
export function getActiveNotifications() {
  const w = getWindow()
  if (!w || !('Notification' in w)) {
    return []
  }
  pruneClosedTracked()
  return trackedNotifications.slice()
}
